/**
 * Session Timeout Utilities
 * 
 * Helper functions for managing session timeout timers and warnings
 */

/**
 * Create a session timeout timer
 * 
 * @param timeoutMinutes - Session timeout duration in minutes (15-120)
 * @param onWarning - Callback function called 2 minutes before expiration, receives time remaining in minutes
 * @param onExpire - Callback function called when session expires
 * @returns Object with clear method to clear both timers
 */
export function createSessionTimer(
  timeoutMinutes: number,
  onWarning: (timeRemainingMinutes: number) => void,
  onExpire: () => void
): { clear: () => void } {
  const timeoutMs = timeoutMinutes * 60 * 1000; // Convert minutes to milliseconds
  const warningLeadMs = 2 * 60 * 1000; // Warn 2 minutes before expiration
  const warningMs = timeoutMs - warningLeadMs;

  // Only schedule a warning when there is a full 2-minute lead time
  const warningTimer =
    warningMs > 0
      ? setTimeout(() => {
          onWarning(2);
        }, warningMs)
      : null;

  // Set expiration timer
  const expirationTimer = setTimeout(() => {
    if (warningTimer) clearTimeout(warningTimer);
    onExpire();
  }, timeoutMs);

  // Return object with clear method to clear both timers
  return {
    clear: () => {
      if (warningTimer) clearTimeout(warningTimer);
      clearTimeout(expirationTimer);
    },
  };
}

/**
 * Clear a session timeout timer
 * 
 * @param timer - Timer object returned from createSessionTimer
 */
export function clearSessionTimer(timer: { clear: () => void } | null): void {
  if (timer) {
    timer.clear();
  }
}

/**
 * Calculate session expiration time
 * 
 * @param timeoutMinutes - Session timeout duration in minutes
 * @returns Date object representing expiration time
 */
export function calculateExpirationTime(timeoutMinutes: number): Date {
  const now = new Date();
  const expirationTime = new Date(now.getTime() + timeoutMinutes * 60 * 1000);
  return expirationTime;
}

/**
 * Check if session is expired
 * 
 * @param expirationTime - Session expiration time
 * @returns True if session is expired
 */
export function isSessionExpired(expirationTime: Date): boolean {
  return new Date() >= expirationTime;
}

/**
 * Get time remaining until session expiration
 * 
 * @param expirationTime - Session expiration time
 * @returns Time remaining in milliseconds (0 if expired)
 */
export function getTimeRemaining(expirationTime: Date): number {
  const now = new Date();
  const remaining = expirationTime.getTime() - now.getTime();
  return Math.max(0, remaining);
}

